import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getCookie } from '../utils/csrf';
import { FaComments } from "react-icons/fa";
import {
  FaLinkedin,
  FaTwitter,
  FaWhatsapp,
  FaYoutube,
  FaEnvelope,
  FaPhone,
  FaUniversity,
  FaClock,
  FaCalendarAlt,
  FaComment,
} from "react-icons/fa";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";


// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatSvcTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const formatSvcDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
};

const isUpcoming = (d) => {
  if (!d) return true; // recurring / no date → always upcoming
  return new Date(d) >= new Date();
};

// ─── Component ───────────────────────────────────────────────────────────────
const Contact = () => {
  // ── Form state ──
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // "success" | "error" | null
  const [submitMessage, setSubmitMessage] = useState("");

  // ── Services state ──
  const [contactServices, setContactServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  // ── Fetch upcoming services (show first 4) ──
  useEffect(() => {
    const fetchServices = async () => {
      setServicesLoading(true);
      try {
        const res = await fetch(`${API_BASE}/services/`, {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        const results = Array.isArray(data) ? data : (data.results ?? []);

        // Filter to upcoming only, then take first 4
        const upcoming = results
          .filter((s) => s.runs_every_day || !s.start_day || isUpcoming(s.start_day))
          .slice(0, 4);

        setContactServices(upcoming);
      } catch (err) {
        console.error("Services fetch error:", err);
        // Fail silently — services panel just stays empty
      } finally {
        setServicesLoading(false);
      }
    };
    fetchServices();
  }, []);

  // ── Form handlers ──
  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    // Basic client-side guard
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.message.trim()) {
      setSubmitStatus("error");
      setSubmitMessage("Please fill in all fields before submitting.");
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage("");

    try {
      // Get CSRF token using the global function from index.html
      const csrftoken = getCookie('csrftoken');

      const res = await fetch(`${API_BASE}/contact/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
          "ngrok-skip-browser-warning": "69420",
        },
        credentials: 'include', // Important for sending cookies
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name:  form.last_name.trim(),
          email:      form.email.trim(),
          messageJson: form.message.trim(), // field name matches serializer
        }),
      });

      if (res.status === 201) {
        const data = await res.json();
        setSubmitStatus("success");
        setSubmitMessage(data.detail || "Thank you for reaching out. We will get back to you soon.");
        setForm({ first_name: "", last_name: "", email: "", message: "" });
      } else {
        const data = await res.json();
        // Surface first validation error if any
        const firstError = Object.values(data)?.[0];
        setSubmitStatus("error");
        setSubmitMessage(
          Array.isArray(firstError) ? firstError[0] : "Something went wrong. Please try again."
        );
      }
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitStatus("error");
      setSubmitMessage("Could not send your message. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');

        :root {
          --navy: #0b1c6b;
          --sky: #159EEC;
          --light-sky: #e8f4fd;
          --dark: #111827;
          --mid: #6b7280;
          --light: #f9fafb;
          --white: #ffffff;
          --border: #e5e7eb;
          --shadow-sm: 0 2px 8px rgba(11,28,107,0.07);
          --shadow-md: 0 8px 30px rgba(11,28,107,0.12);
          --shadow-lg: 0 20px 60px rgba(11,28,107,0.15);
        }

        .cp * { box-sizing: border-box; margin: 0; padding: 0; }
        .cp { font-family: 'Lexend', sans-serif; color: var(--dark); background: var(--white); overflow-x: hidden; }

        /* HERO */
        .cp-hero { position: relative; height: 380px; background: url('/images/p.png') center/cover no-repeat; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .cp-hero::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(11,28,107,0.92) 0%, rgba(21,158,236,0.80) 100%); }
        .cp-hero-content { position: relative; text-align: center; color: var(--white); padding: 0 20px; }
        .cp-hero-content h1 { font-family: 'Lexend', sans-serif; font-size: clamp(2.4rem, 5vw, 3.8rem); font-weight: 800; line-height: 1.1; margin-bottom: 14px; letter-spacing: -0.5px; }
        .cp-hero-content p { font-size: 1.05rem; color: rgba(255,255,255,0.85); font-weight: 300; max-width: 480px; margin: 0 auto; line-height: 1.7; }
        .cp-hero-badge { display: inline-block; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.3); color: #fff; font-size: 0.75rem; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; padding: 6px 18px; border-radius: 50px; margin-bottom: 18px; }

        /* LAYOUT */
        .cp-section { max-width: 1200px; margin: 0 auto; padding: 80px 24px; }
        .cp-section-sm { max-width: 1200px; margin: 0 auto; padding: 60px 24px; }
        .cp-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: var(--sky); margin-bottom: 10px; }
        .cp-heading { font-family: 'Lexend', sans-serif; font-size: clamp(1.8rem, 3.5vw, 2.8rem); font-weight: 700; color: var(--navy); line-height: 1.2; margin-bottom: 16px; }
        .cp-subtext { color: var(--mid); font-size: 0.98rem; line-height: 1.8; font-weight: 300; max-width: 460px; }
        .cp-divider { width: 60px; height: 4px; background: linear-gradient(90deg, var(--navy), var(--sky)); border-radius: 4px; margin: 16px 0 32px; }

        /* FORM GRID */
        .cp-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 70px; align-items: center; }
        .cp-form { display: flex; flex-direction: column; gap: 0; }
        .cp-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .cp-field { margin-bottom: 18px; }
        .cp-field label { display: block; font-size: 0.78rem; font-weight: 600; letter-spacing: 0.5px; color: var(--navy); margin-bottom: 7px; text-transform: uppercase; }
        .cp-field input, .cp-field textarea { width: 100%; padding: 14px 18px; border: 1.5px solid var(--border); border-radius: 12px; font-family: 'Lexend', sans-serif; font-size: 0.95rem; color: var(--dark); background: var(--light); outline: none; transition: border-color 0.25s, box-shadow 0.25s, background 0.25s; }
        .cp-field input:focus, .cp-field textarea:focus { border-color: var(--sky); background: var(--white); box-shadow: 0 0 0 4px rgba(21,158,236,0.12); }
        .cp-field textarea { height: 160px; resize: none; }

        .cp-submit-btn { background: linear-gradient(135deg, var(--navy), var(--sky)); color: white; border: none; padding: 16px 36px; border-radius: 12px; font-family: 'Lexend', sans-serif; font-weight: 600; font-size: 0.95rem; cursor: pointer; letter-spacing: 0.5px; transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s; box-shadow: 0 6px 20px rgba(11,28,107,0.25); align-self: flex-start; }
        .cp-submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(11,28,107,0.3); }
        .cp-submit-btn:disabled { opacity: 0.65; cursor: not-allowed; }

        /* FEEDBACK BANNERS */
        .cp-feedback { border-radius: 10px; padding: 14px 18px; font-size: 0.9rem; font-weight: 500; margin-bottom: 20px; }
        .cp-feedback.success { background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; }
        .cp-feedback.error   { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }

        .cp-privacy { margin-top: 14px; font-size: 0.8rem; color: var(--mid); line-height: 1.6; }

        /* MAP */
        .cp-map-wrap { border-radius: 20px; overflow: hidden; box-shadow: var(--shadow-md); height: 480px; }
        .cp-map-wrap iframe { width: 100%; height: 100%; border: none; display: block; }
        .cp-map-header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 28px; flex-wrap: wrap; gap: 16px; }
        .cp-directions-btn { background: var(--white); color: var(--navy); border: 2px solid var(--navy); padding: 12px 28px; border-radius: 50px; font-family: 'Lexend', sans-serif; font-weight: 600; font-size: 0.88rem; cursor: pointer; letter-spacing: 0.5px; transition: all 0.25s; white-space: nowrap; }
        .cp-directions-btn:hover { background: var(--navy); color: white; transform: translateY(-2px); box-shadow: var(--shadow-md); }

        /* INQUIRIES */
        .cp-inquiries-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 70px; align-items: center; }
        .cp-inquiry-img { border-radius: 20px; overflow: hidden; height: 540px; box-shadow: var(--shadow-lg); }
        .cp-inquiry-img img { width: 100%; height: 100%; object-fit: cover; }
        .cp-inquiry-items { display: flex; flex-direction: column; gap: 0; }
        .cp-inquiry-item { display: flex; align-items: center; justify-content: space-between; padding: 26px 0; border-bottom: 1px solid var(--border); cursor: pointer; transition: all 0.2s; }
        .cp-inquiry-item:first-child { border-top: 1px solid var(--border); }
        .cp-inquiry-item:hover .cp-inquiry-text { color: var(--sky); transform: translateX(6px); }
        .cp-inquiry-item:hover .cp-inquiry-arrow { background: var(--sky); color: white; }
        .cp-inquiry-text { font-weight: 600; font-size: 1rem; color: var(--navy); letter-spacing: 0.3px; transition: all 0.25s; }
        .cp-inquiry-arrow { width: 36px; height: 36px; border-radius: 50%; background: var(--light-sky); color: var(--sky); display: flex; align-items: center; justify-content: center; font-size: 1rem; transition: all 0.25s; flex-shrink: 0; }

        /* CONTACT DETAILS */
        .cp-details-bg { background: linear-gradient(135deg, var(--navy) 0%, #1a4ab5 100%); position: relative; overflow: hidden; }
        .cp-details-bg::before { content: ''; position: absolute; top: -100px; right: -100px; width: 400px; height: 400px; border-radius: 50%; background: rgba(21,158,236,0.15); }
        .cp-details-bg::after { content: ''; position: absolute; bottom: -80px; left: -60px; width: 300px; height: 300px; border-radius: 50%; background: rgba(255,255,255,0.04); }
        .cp-details-inner { position: relative; z-index: 1; }
        .cp-details-heading { font-family: 'Lexend', sans-serif; font-size: clamp(1.8rem, 3vw, 2.5rem); font-weight: 700; color: white; margin-bottom: 10px; }
        .cp-details-sub { color: rgba(255,255,255,0.65); font-size: 0.95rem; font-weight: 300; margin-bottom: 48px; }
        .cp-details-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; }
        .cp-detail-card { background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); border-radius: 16px; padding: 28px 24px; backdrop-filter: blur(10px); transition: all 0.3s; }
        .cp-detail-card:hover { background: rgba(255,255,255,0.14); transform: translateY(-4px); border-color: rgba(21,158,236,0.5); }
        .cp-detail-icon { width: 44px; height: 44px; background: rgba(21,158,236,0.25); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; margin-bottom: 18px; color: var(--sky); }
        .cp-detail-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--sky); margin-bottom: 10px; }
        .cp-detail-value { color: rgba(255,255,255,0.9); font-size: 0.93rem; line-height: 1.8; font-weight: 300; }

        /* SOCIAL */
        .cp-social { background: var(--light); border-top: 1px solid var(--border); }
        .cp-social-inner { max-width: 700px; margin: 0 auto; text-align: center; padding: 70px 24px; }
        .cp-social-inner h3 { font-family: 'Lexend', sans-serif; font-size: 1.8rem; color: var(--navy); font-weight: 700; margin-bottom: 10px; }
        .cp-social-inner p { color: var(--mid); font-size: 0.93rem; margin-bottom: 36px; font-weight: 300; }
        .cp-social-links { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; }
        .cp-social-link { display: flex; align-items: center; gap: 10px; padding: 12px 22px; border-radius: 50px; border: 1.5px solid var(--border); color: var(--navy); text-decoration: none; font-size: 0.88rem; font-weight: 600; background: white; transition: all 0.25s; box-shadow: var(--shadow-sm); }
        .cp-social-link svg { font-size: 1.1rem; }
        .cp-social-link:hover { transform: translateY(-3px); box-shadow: var(--shadow-md); }
        .cp-social-link.linkedin:hover  { background: #0077b5; color: white; border-color: #0077b5; }
         .cp-social-link.imo:hover  { background: #64c7fc; color: white; border-color: #0077b5; }
        .cp-social-link.twitter:hover   { background: #000; color: white; border-color: #000; }
        .cp-social-link.whatsapp:hover  { background: #25D366; color: white; border-color: #25D366; }
        .cp-social-link.youtube:hover   { background: #FF0000; color: white; border-color: #FF0000; }

        /* SERVICE CARDS PANEL */
        .cp-services-panel { display: flex; flex-direction: column; gap: 0; align-self: stretch; }
        .cp-services-title { font-size: 1.4rem; font-weight: 700; color: var(--navy); margin-bottom: 20px; }
        .cp-services-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; flex: 1; }
        .cp-svc-card { display: flex; align-items: stretch; background: var(--white); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; box-shadow: var(--shadow-sm); transition: transform 0.2s, box-shadow 0.2s; }
        .cp-svc-card:hover { transform: translateX(4px); box-shadow: var(--shadow-md); }
        .cp-svc-accent { width: 5px; flex-shrink: 0; background: linear-gradient(180deg, var(--navy), var(--sky)); }
        .cp-svc-body { padding: 14px 16px; display: flex; flex-direction: column; gap: 4px; }
        .cp-svc-date { font-size: 0.72rem; font-weight: 600; color: var(--sky); letter-spacing: 0.3px; }
        .cp-svc-name { font-size: 0.93rem; font-weight: 700; color: var(--navy); line-height: 1.3; }
        .cp-svc-time { font-size: 0.78rem; color: var(--mid); font-weight: 400; }
        .cp-svc-empty { font-size: 0.88rem; color: var(--mid); text-align: center; padding: 28px 0; }

        .cp-view-services-btn { width: 100%; background: linear-gradient(135deg, var(--navy), var(--sky)); color: #fff; border: none; padding: 14px; border-radius: 12px; font-family: 'Lexend', sans-serif; font-weight: 600; font-size: 0.92rem; cursor: pointer; letter-spacing: 0.3px; transition: opacity 0.2s, transform 0.2s; box-shadow: 0 6px 20px rgba(11,28,107,0.25); }
        .cp-view-services-btn:hover { opacity: 0.9; transform: translateY(-2px); }

        /* RESPONSIVE */
        @media (max-width: 900px) {
          .cp-form-grid, .cp-inquiries-grid { grid-template-columns: 1fr; gap: 40px; }
          .cp-inquiry-img { height: 320px; order: -1; }
        }
        @media (max-width: 600px) {
          .cp-form-row { grid-template-columns: 1fr; }
          .cp-hero { height: 300px; }
          .cp-section, .cp-section-sm { padding: 50px 20px; }
          .cp-map-header { flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      <div className="cp">

        {/* HERO */}
        <section className="cp-hero">
          <div className="cp-hero-content">
            <span className="cp-hero-badge">Gospel City Church International</span>
            <h1>Get In Touch</h1>
            <p>We'd love to hear from you. Reach out for prayer, guidance, or simply to connect with our family.</p>
          </div>
        </section>

        {/* CONTACT FORM + SERVICES PANEL */}
        <section className="cp-section">
          <div className="cp-form-grid">

            {/* ── LEFT: Form ── */}
            <div>
              <p className="cp-label">Send a message</p>
              <h2 className="cp-heading">We'd Love to<br />Hear From You</h2>
              <div className="cp-divider"></div>
              <p className="cp-subtext" style={{ marginBottom: "36px" }}>
                Get in touch with us. We're here to address your inquiries and give you all the information you need.
              </p>

              {/* Feedback banner */}
              {submitStatus && (
                <div className={`cp-feedback ${submitStatus}`}>
                  {submitMessage}
                </div>
              )}

              <div className="cp-form">
                <div className="cp-form-row">
                  <div className="cp-field">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="first_name"
                      placeholder="e.g. Sarah"
                      value={form.first_name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="cp-field">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      placeholder="e.g. Nakato"
                      value={form.last_name}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="cp-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="cp-field">
                  <label>Your Message</label>
                  <textarea
                    name="message"
                    placeholder="Write your message here..."
                    value={form.message}
                    onChange={handleChange}
                  ></textarea>
                </div>

                <button
                  className="cp-submit-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Sending…" : "Send Your Message →"}
                </button>
                <p className="cp-privacy">
                  We handle your contact details in accordance with our Privacy Policy.
                </p>
              </div>
            </div>

            {/* ── RIGHT: Services Panel ── */}
            <div className="cp-services-panel">
              <p className="cp-label">Upcoming at GCCI</p>
              <h3 className="cp-services-title">Services &amp; Events</h3>

              <div className="cp-services-list">
                {servicesLoading ? (
                  <p className="cp-svc-empty">Loading…</p>
                ) : contactServices.length === 0 ? (
                  <p className="cp-svc-empty">No upcoming services right now. Check back soon!</p>
                ) : (
                  contactServices.map((s) => {
                    // Label for recurring services
                    let dateLabel = "";
                    if (s.is_single_day && s.start_day)  dateLabel = formatSvcDate(s.start_day);
                    else if (s.runs_every_day)            dateLabel = "Every Day";
                    else if (s.day_of_week_display)       dateLabel = `Every ${s.day_of_week_display}`;
                    else                                  dateLabel = "Ongoing";

                    return (
                      <div className="cp-svc-card" key={s.id}>
                        <div className="cp-svc-accent"></div>
                        <div className="cp-svc-body">
                          <div className="cp-svc-date">
                            <FaCalendarAlt /> {dateLabel}
                          </div>
                          <div className="cp-svc-name">{s.title}</div>
                          <div className="cp-svc-time">
                            <FaClock /> {formatSvcTime(s.start_time)}
                            {s.end_time && ` – ${formatSvcTime(s.end_time)}`}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <Link to="/church-services" style={{ textDecoration: "none" }}>
                <button className="cp-view-services-btn">View All Services →</button>
              </Link>
            </div>
          </div>
        </section>

        {/* MAP */}
        <section className="cp-section-sm" style={{ paddingTop: 0 }}>
          <div className="cp-map-header">
            <div>
              <p className="cp-label">Our location</p>
              <h2 className="cp-heading" style={{ marginBottom: 0 }}>Find Us on the Map</h2>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Gospel+City+Church+International"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="cp-directions-btn">↗ Get Directions</button>
            </a>
          </div>
          <div className="cp-map-wrap">
            <iframe
              title="Church Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3979.226871882856!2d32.57759881464651!3d0.3272385998105084!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x177dbed4b16aa20f%3A0x79ff989ebcdcb1c6!2sGospel%20City%20Church%20International!5e0!3m2!1sen!2sug!4v1705833582000!5m2!1sen!2sug"
              loading="lazy"
            ></iframe>
          </div>
        </section>

        {/* INQUIRIES */}
        <section className="cp-section" style={{ background: "#f9fafb" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <div className="cp-inquiries-grid">
              <div className="cp-inquiry-img">
                <img src="/images/p6.jpg" alt="Inquiries" />
              </div>
              <div>
                <p className="cp-label">How can we help?</p>
                <h2 className="cp-heading">Other Inquiries</h2>
                <div className="cp-divider"></div>
                <div className="cp-inquiry-items">
                  {[
                    "Teaching About One True God",
                    "Need Prayer?",
                    "Seeking Guidance?",
                    "Counselling Services",
                  ].map((item) => (
                    <div className="cp-inquiry-item" key={item}>
                      <span className="cp-inquiry-text">{item}</span>
                      <span className="cp-inquiry-arrow">→</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT DETAILS */}
        <section className="cp-details-bg">
          <div className="cp-section cp-details-inner">
            <p className="cp-label" style={{ color: "rgba(21,158,236,0.9)" }}>Reach out directly</p>
            <h2 className="cp-details-heading">Contact Details</h2>
            <p className="cp-details-sub">Multiple ways to get in touch with our team</p>
            <div className="cp-details-cards">
              {[
                { icon: <FaWhatsapp />, label: "WhatsApp & IMO", value: "+256 783 359 816" },
                { icon: <FaPhone />, label: "Pastors", value: "+256 775 257 051\n+256 772 871 632" },
                { icon: <FaUniversity />, label: "Administrators", value: "+256 775 028 569\n+256 782 987 207" },
                { icon: <FaEnvelope />, label: "Pastors Emails", value: "faithmuwanguzi@gmail.com\nmuwanguzikeffah@gmail.com" },
              ].map((d) => (
                <div className="cp-detail-card" key={d.label}>
                  <div className="cp-detail-icon">{d.icon}</div>
                  <div className="cp-detail-label">{d.label}</div>
                  <div className="cp-detail-value">
                    {d.value.split("\n").map((line, i, arr) => (
                      <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SOCIAL */}
        <section className="cp-social">
          <div className="cp-social-inner">
            <h3>Connect With Us</h3>
            <p>Stay updated and join the conversation on our social platforms.</p>
            <div className="cp-social-links">
              <a href="https://www.linkedin.com/in/gospel-city-church-international-650a562a0/" target="_blank" rel="noopener noreferrer" className="cp-social-link linkedin"><FaLinkedin /> LinkedIn</a>
              <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="cp-social-link twitter"><FaTwitter /> Twitter / X</a>
              <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="cp-social-link whatsapp"><FaWhatsapp /> WhatsApp</a>
              <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="cp-social-link youtube"><FaYoutube /> YouTube</a>
              <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="cp-social-link imo"><FaComments/> IMO </a>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Contact;